import { useCallback } from 'react';
import { fetchToken, useLayers, useSearchResultFeature } from '@agissc/ktag-map';
import { useTranslation } from 'react-i18next';
import useAppState from '../../../AppStore';
import { useMetadataQueries } from '../../mapContent/api/useMetadataQuery';
import { useAttributeSearchData } from '../../hooks/useAttributeSearch';
import usePdfUrl from './usePdfUrl';
import { DEFAULT_PRINT_PAYLOAD } from './contracts';
import {
  mapToGraphics,
  searchToGraphic,
  attributeSearchToGraphic,
} from './mapToGraphics';
import { getMapService, getPrintExtent } from './printHelpers';
import { useTracking } from '../../../utils/tracking/TrackingContext';

interface UsePrintSubmitParams {
  format: any;
  visibleLayersRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
  setPrintButtonDisabled: (disabled: boolean) => void;
}

const usePrintSubmit = ({
  format,
  visibleLayersRefs,
  setPrintButtonDisabled,
}: UsePrintSubmitParams) => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();

  const location = useAppState((state) => state.location);
  const basemapId = useAppState((state) => state.selectedBasemapId);
  const isBasemapVisible = useAppState((state) => state.isBasemapVisible);
  const basemapOpacity = useAppState((state) => state.basemapOpacity);
  const topicLayerSettings = useAppState((state) => state.topicLayerSettings);
  const topicSubLayerSettings = useAppState((state) => state.topicSubLayerSetting);
  const layoutTitle = useAppState((state) => state.layoutTitle);
  const layoutSubTitle = useAppState((state) => state.layoutSubTitle);
  const printLegend = useAppState((state) => state.printLegend);
  const printScale = useAppState((state) => state.printScale);
  const layoutId = useAppState((state) => state.layoutId);
  const formatValue = useAppState((state) => state.formatValue);
  const orientation = useAppState((state) => state.orientation);
  const angle = useAppState((state) => state.angle);
  const printMapOnly = useAppState((state) => state.printMapOnly);
  const drawFeatures = useAppState((state) => state.drawFeatures);
  const isLoggedIn = useAppState((state) => state.isLoggedIn);
  const currentAttributeSearchTopicId = useAppState(
    (state) => state.currentAttributeSearchTopicId,
  );
  const attributeSearch = useAppState((state) => state.attributeSearch);
  const addPrintedItem = useAppState((state) => state.addPrintedItem);
  const addNotification = useAppState((state) => state.addNotification);

  const layers = useLayers();
  const topicLayers = layers.data ?? {};
  const selectedTopicLayers = useAppState((state) => state.selectedTopicLayers).filter(
    (id) => topicLayers[id],
  );
  const topicLayersProps = selectedTopicLayers
    .filter((layer) => topicLayers[layer]?.type === 'esriMapServer')
    .map((id) => ({ id, isPublic: topicLayers[id]?.isPublic ?? true }));

  const layersConfigQueries = useMetadataQueries(topicLayersProps);
  const searchFeature = useSearchResultFeature();
  const attributeQueryData = useAttributeSearchData(currentAttributeSearchTopicId);

  const onSubmit = useCallback(async () => {
    setPrintButtonDisabled(true);
    Tracking.Print.print();

    const visibleLayers = selectedTopicLayers.filter(
      (id) => topicLayerSettings[id].isVisible,
    );

    const topicServices = visibleLayers.map((id) => {
      const topicLayer = topicLayers[id];
      const { opacity, isVisible } = topicLayerSettings[id];

      const layerRefIds = visibleLayersRefs.current[id]?.value;
      const visibleLayerIds = layerRefIds
        ? layerRefIds.split(',').filter(Boolean).map(Number)
        : [];

      const layersInLegend: number[] =
        visibleLayerIds?.filter((layerId) => {
          const layerConfig = layersConfigQueries.data[id];
          if (!layerConfig) return false;
          return layerConfig[layerId]?.displayInLegend;
        }) ?? [];

      return getMapService({
        topicLayer,
        opacity,
        isVisible,
        layersInLegend,
        visibleLayerIds,
      });
    });

    const basemapService = getMapService({
      topicLayer: topicLayers[basemapId],
      opacity: basemapOpacity,
      isVisible: isBasemapVisible,
      isBasemap: true,
    });

    const mapServices = [basemapService, ...topicServices.reverse()];
    const { mapHeightInMillimeter, mapWidthInMillimeter } = format?.[orientation] ?? {
      mapHeightInMillimeter: 0,
      mapWidthInMillimeter: 0,
    };

    const printWidth = (mapWidthInMillimeter * printScale) / 1000;
    const printHeight = (mapHeightInMillimeter * printScale) / 1000;
    const printExtent = getPrintExtent(
      location.center,
      location.extent,
      printWidth,
      printHeight,
    );

    const searchGraphic = searchFeature.data
      ? await searchToGraphic(searchFeature.data)
      : [];
    const graphics = mapToGraphics(drawFeatures, printScale, angle);

    const searchParams = currentAttributeSearchTopicId
      ? attributeSearch[currentAttributeSearchTopicId]
      : null;

    const attributeSearchToGraphics = attributeQueryData
      ? await attributeSearchToGraphic(
          attributeQueryData.features,
          searchParams?.selectedFeatureId,
          printExtent,
        )
      : [];

    const payload = {
      ...DEFAULT_PRINT_PAYLOAD,
      Graphics: graphics.concat(attributeSearchToGraphics, searchGraphic),
      Layouttitle: layoutTitle,
      Layoutsubtitle: layoutSubTitle,
      LegendVisible: printMapOnly ? false : printLegend,
      Scale: printScale,
      LayoutOrientation: orientation === 'landscape' ? 'quer' : 'hoch',
      Layoutformat: formatValue,
      LayoutId: layoutId,
      Height: mapHeightInMillimeter,
      Width: mapWidthInMillimeter,
      Angle: angle,
      MapExtent: printExtent,
      MapServices: mapServices,
      MapOnly: printMapOnly,
    };

    const token = isLoggedIn ? await fetchToken() : null;
    const { url, error } = await usePdfUrl(payload, token);

    if (url) {
      window.open(url, '_blank');
      const label = `${layoutTitle}_${formatValue}_${
        orientation === 'landscape' ? 'Q' : 'H'
      }_1:${printScale}`;
      addPrintedItem(label, url, printMapOnly ? 'picture' : 'file-pdf');
    } else if (error) {
      Tracking.Error.printError(error);
      addNotification({
        id: Date.now().toString(),
        title: 'Fehler beim Drucken',
        message: `${t('notification_print_failed')}\n        ${error}`,
        type: 'negative',
      });
    }

    setPrintButtonDisabled(false);
  }, [
    layoutTitle,
    layoutSubTitle,
    printLegend,
    location.extent,
    printScale,
    formatValue,
    orientation,
    selectedTopicLayers,
    topicLayerSettings,
    topicLayers,
    topicSubLayerSettings,
    printMapOnly,
    angle,
    layersConfigQueries,
    visibleLayersRefs,
  ]);

  return { onSubmit, topicLayers, selectedTopicLayers, layersConfigQueries };
};

export default usePrintSubmit;
